import { useState } from 'react';
import { PropTypes } from 'prop-types';
import { InputControlled } from 'components/UI/InputControlled/InputControlled';
import { DropdownWithMenu } from 'components/UI/DropdownWithMenu/DropdownWithMenu';
import { useFormContext } from 'react-hook-form';
import { getValue } from 'utils/form';
import { getStates } from 'services/UserAccountService';

export const AddressInformation = (props) => {
  const { idPrefix = '', namePrefix = '', countryStates, countries, header } = props;
  const [retainFocusList, setRetainFocusList] = useState([]);

  const { setValue, getValues } = useFormContext();

  const countryDropDownOptions = countries.map((country) => ({
    key: country.Description,
    name: country.Description,
  }));

  // State management
  const [stateOptions, setStateOptions] = useState(countryStates);
  const [selectedStateDescription, setSelectedStateDescription] = useState(
    getValue(getValues(), `${idPrefix}stateDescription`)
  );

  const dropdownStateOptions = stateOptions
    .filter((stateItem) => !stateItem.Inactive)
    .map((stateItem) => ({
      key: stateItem.StateCode || stateItem.stateCode,
      name: stateItem.Description || stateItem.description,
    }));

  /**
   * Handle State Drop Down Change
   */
  const stateDropdownUpdateHandler = (stateCode) => {
    const refState = stateOptions.find(
      (stateItem) => (stateItem.StateCode || stateItem.stateCode) === stateCode
    );
    setValue(`${idPrefix}state`, refState.StateCode || refState.stateCode, { shouldDirty: true });
    setValue(`${idPrefix}stateDescription`, refState.Description || refState.description, {
      shouldDirty: true,
    });
    setValue(`${idPrefix}stateId`, refState.Id || refState.id, { shouldDirty: true });
  };

  const setStatesEmpty = async (label = 'N/A') => {
    setStateOptions([{ key: null, name: label }]);
    await setSelectedStateDescription(label);
    setValue(`${idPrefix}state`, label, { shouldDirty: true });
    setValue(`${idPrefix}stateDescription`, label, { shouldDirty: true });
    setValue(`${idPrefix}stateId`, 0, { shouldDirty: true });
  };

  /**
   * Handle Country Drop Down Change
   */
  const countryDropdownUpdateHandler = async (country) => {
    const refCountry = countries.find((refCountryItem) => refCountryItem.Description === country);
    setValue(`${idPrefix}country`, refCountry.Description);
    setValue(`${idPrefix}countryId`, refCountry.Id);

    try {
      await setStatesEmpty(' ');
      const refStates = await getStates(refCountry.Id);

      if (!refStates || !refStates.length) {
        setStatesEmpty();
        return;
      }

      setStateOptions(refStates);
      setSelectedStateDescription(refStates[0].description);
      setValue(`${idPrefix}state`, refStates[0].stateCode, { shouldDirty: true });
      setValue(`${idPrefix}stateDescription`, refStates[0].description, {
        shouldDirty: true,
      });
      setValue(`${idPrefix}stateId`, refStates[0].id, { shouldDirty: true });
    } catch {
      setStatesEmpty();
    }
  };

  const onFocusInputHandler = (inputIdPrefix, id) => {
    if (!inputIdPrefix) {
      return;
    }

    setRetainFocusList((prevState) => (!prevState.includes(id) ? [...prevState, id] : prevState));
  };

  return (
    <div className="form-section">
      {header}

      <div className="form-group">
        <InputControlled
          type="text"
          labelText="Address Line 1 "
          id={`${idPrefix}deliveryAddress1`}
          name={`${namePrefix}deliveryAddress1`}
          retainFocus={retainFocusList.includes('deliveryAddress1')}
          onFocusInputHandler={() => onFocusInputHandler(idPrefix, 'deliveryAddress1')}
        />
      </div>

      <div className="form-group">
        <InputControlled
          type="text"
          labelText="Address Line 2"
          id={`${idPrefix}deliveryAddress2`}
          retainFocus={retainFocusList.includes('deliveryAddress2')}
          onFocusInputHandler={() => onFocusInputHandler(idPrefix, 'deliveryAddress2')}
        />
      </div>

      <div className="form-group">
        <InputControlled
          type="text"
          labelText="Suburb"
          id={`${idPrefix}suburb`}
          retainFocus={retainFocusList.includes('suburb')}
          onFocusInputHandler={() => onFocusInputHandler(idPrefix, 'suburb')}
        />
      </div>

      <div className="form-group">
        <InputControlled
          type="text"
          labelText="Postcode"
          id={`${idPrefix}postcode`}
          retainFocus={retainFocusList.includes('postcode')}
          onFocusInputHandler={() => onFocusInputHandler(idPrefix, 'postcode')}
        />
      </div>

      <div className="form-group">
        <DropdownWithMenu
          singleColumn
          hideDefaultItem
          dropdownChangeHandler={stateDropdownUpdateHandler}
          menuData={dropdownStateOptions}
          defaultChosenValue={selectedStateDescription}
          updatedChosenValue={selectedStateDescription}
        />
        <InputControlled type="hidden" id={`${idPrefix}stateId`} />
        <InputControlled type="hidden" id={`${idPrefix}stateDescription`} />
        <InputControlled type="hidden" id={`${idPrefix}state`} />
      </div>

      <div className="form-group">
        <DropdownWithMenu
          singleColumn
          hideDefaultItem
          dropdownChangeHandler={countryDropdownUpdateHandler}
          menuData={countryDropDownOptions}
          defaultChosenValue={getValue(getValues(), `${idPrefix}country`)}
        />
        <InputControlled type="hidden" id={`${idPrefix}country`} />
        <InputControlled type="hidden" id={`${idPrefix}countryId`} />
      </div>
    </div>
  );
};

AddressInformation.defaultValues = {
  idPrefix: '',
  namePrefix: '',
  header: null,
};

AddressInformation.propTypes = {
  idPrefix: PropTypes.string,
  namePrefix: PropTypes.string,
  countryStates: PropTypes.array.isRequired,
  countries: PropTypes.array.isRequired,
  header: PropTypes.element,
};
